import React, { FC, useCallback, useState, useMemo } from 'react';
import { Button, Form, Input, message, Modal, Switch } from 'antd';
import {
	savedFilterViewsRestCrudThunksForBuyer,
	savedFilterViewsRestCrudThunksForSupplier,
} from '../../thunks/saved_filter_views_thunks';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
	multiValueFilterFieldNames,
	ROLE_TYPES,
	VIEW_ENTITY_TYPES,
} from '../../utils/DataConstants';
import { BUYER_ADMIN_ONLY, SUPPLIER_ADMIN_ONLY } from '../roles/roles';
import {
	cleanFiltersForView,
	compareStrings,
	getEntityById,
	nullSafeGet,
} from '../../utils/DataAccessUtils';
import OWRadioGroup from '../ow_radio_group/OWRadioGroup';
import {
	getAttentionDashboard,
	updateAttentionDashboard,
} from '../../thunks/attention_dashboard_thunks';

interface SaveFiltersFormProps {
	filters: any;
	roleType: 'buyer' | 'supplier';
	entityType: String;
	preAppliedFilters: any;
	onCancel: () => void;
	currentUser?: any;
	attentionDashboards?: any;
	saveAttentionDashboard?: (entity: any) => any;
	saveFilterView?: (entity: any) => any;
	updateFilterView?: (entity: any) => any;
	fetchWorkOrderViews?: any;
	viewId?: string;
	view?: any;
	defaultView?: any;
	skipFilterUpdate?: boolean;
}

const SaveFiltersPopup: FC<SaveFiltersFormProps> = ({
	filters,
	roleType,
	entityType,
	currentUser,
	preAppliedFilters,
	onCancel,
	saveFilterView,
	updateFilterView,
	attentionDashboards,
	saveAttentionDashboard,
	viewId,
	view,
	defaultView,
	skipFilterUpdate,
	fetchWorkOrderViews,
}): React.ReactElement => {
	const [form] = Form.useForm();

	const [saving, setSaving] = useState(false);
	const isUpdateMode = !!viewId;

	const initialValues = useMemo(() => {
		if (isUpdateMode && view) {
			return {
				name: view.name || '',
				isPublic: view.isPublic ? 'true' : 'false',
				isDefault: nullSafeGet('id', defaultView) === nullSafeGet('id', view),
			};
		}
		return {
			name: '',
			isPublic: 'false',
			isDefault: nullSafeGet('id', defaultView)
				? nullSafeGet('id', defaultView) === nullSafeGet('id', view)
				: false,
		};
	}, [isUpdateMode, view, defaultView]);

	const isAdmin = useMemo(() => {
		const roleList = roleType === ROLE_TYPES.SUPPLIER ? SUPPLIER_ADMIN_ONLY : BUYER_ADMIN_ONLY;
		return currentUser.roles.some((role) => roleList.has(role));
	}, [roleType, currentUser]);

	const handleSubmit = useCallback(
		(values) => {
			setSaving(true);
			const filterPayload = skipFilterUpdate
				? {}
				: (() => {
						const cleanedFilters = cleanFiltersForView(filters, preAppliedFilters);
						const filterValue = Object.keys(cleanedFilters).reduce((obj, key) => {
							multiValueFilterFieldNames.includes(key)
								? (obj[key] = cleanedFilters[key].split(','))
								: (obj[key] = cleanedFilters[key]);
							return obj;
						}, {});
						return {
							filterValue,
							filterQuery: new URLSearchParams(cleanedFilters).toString(),
						};
				  })();
			// When isDefault is checked, also set isPinned to true so it appears as a tab
			const isPinnedPayload = values.isDefault ? { isPinned: true } : {};
			const payload = {
				...(isUpdateMode ? { id: viewId } : {}),
				...view,
				name: values.name,
				entityType,
				isPublic: values.isPublic === 'true',
				...filterPayload,
				...isPinnedPayload,
			};
			const savePromise = isUpdateMode ? updateFilterView(payload) : saveFilterView(payload);
			savePromise
				.then((data) => {
					if (compareStrings(entityType, 'work_orders') && !!values.isDefault && data.id) {
						saveAttentionDashboard({
							...attentionDashboards,
							defaultWorkOrderFilterViewId: data.id,
						}).then(() => {
							fetchWorkOrderViews(
								null,
								'workOrdersSavedFilterPinnedViews',
								{ current: 1, pageSize: 100 },
								{ sort_by: 'order', order: 'ascend' },
								{ entityType: VIEW_ENTITY_TYPES.WORKORDERS, isPinned: true },
								undefined
							).catch(() => {});
							onCancel();
						});
					} else {
						onCancel();
					}
				})
				.catch((err) => {
					setSaving(false);
					message.error('Failed to save view');
				});
		},
		[
			entityType,
			preAppliedFilters,
			filters,
			saveFilterView,
			updateFilterView,
			onCancel,
			isUpdateMode,
			viewId,
			attentionDashboards,
			saveAttentionDashboard,
			skipFilterUpdate,
			view,
			fetchWorkOrderViews,
		]
	);

	return (
		<Modal
			visible={true}
			title={isUpdateMode ? 'Update this View' : 'Save this View'}
			onCancel={onCancel}
			footer={[
				<Button key="cancel" onClick={onCancel}>
					Cancel
				</Button>,
				<Button key="submit" type="primary" loading={saving} onClick={() => form.submit()}>
					{isUpdateMode ? 'Update' : 'Save'}
				</Button>,
			]}
			closable={true}
		>
			<Form
				form={form}
				layout="vertical"
				initialValues={initialValues}
				requiredMark={false}
				onFinish={handleSubmit}
			>
				<Form.Item
					name="name"
					label="Name"
					rules={[{ required: true, message: 'Please enter a name for the view!' }]}
				>
					<Input type="text" placeholder="Enter Name of this view" />
				</Form.Item>
				{isAdmin && (
					<Form.Item name="isPublic">
						{/* @ts-ignore */}
						<OWRadioGroup
							items={[
								{
									label: 'Private',
									value: 'false',
								},
								{
									label: 'Public',
									value: 'true',
								},
							]}
							valueAccessor={(item) => item.value}
							renderRecord={(item) => item.label}
							info={[
								{
									title: 'Private',
									description: 'Visible only to you.',
								},
								{
									title: 'Public',
									description: 'Visible to everyone in your team.',
								},
							]}
						/>
					</Form.Item>
				)}
				{compareStrings(entityType, 'work_orders') ? (
					<Form.Item name="isDefault" valuePropName={'checked'} label="Make Default View">
						<Switch />
					</Form.Item>
				) : null}
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
	attentionDashboards: state.attention_dashboards.detail,
	view: ownProps.viewId ? getEntityById(ownProps.viewId, state.saved_filter_views) : undefined,
	defaultView: state.session.defaultView,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getAttentionDashboard: () => getAttentionDashboard(dispatch, ownProps.userType),
	saveFilterView: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? savedFilterViewsRestCrudThunksForSupplier.create(entity)
				: savedFilterViewsRestCrudThunksForBuyer.create(entity)
		),
	updateFilterView: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? savedFilterViewsRestCrudThunksForSupplier.update(entity)
				: savedFilterViewsRestCrudThunksForBuyer.update(entity)
		),
	saveAttentionDashboard: (entity) => updateAttentionDashboard(dispatch, ownProps.userType, entity),
	fetchWorkOrderViews: (
		params,
		targetCollectionName,
		pagination = { current: 1, pageSize: 100 },
		sorting?,
		filters?,
		addToTargetCollection?
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? savedFilterViewsRestCrudThunksForSupplier.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: savedFilterViewsRestCrudThunksForBuyer.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
});
const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(SaveFiltersPopup)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
